import { useMutation, useQuery } from '@tanstack/react-query';
import {
    abortAllCronJobs,
    abortCronJob,
    getCronJobHistory,
    getCronJobs,
    getCronQueueJobs,
    getCronQueueStatus,
    getCronRunSteps,
    promoteCronQueueJob,
    removeCronQueueJob,
    runCronJob,
    setCronJobLane,
    toggleCronJob,
    updateCronJobSchedule,
    updateCronJobTimeout,
} from './cronApi';

export const createCronHooks = ({ queryClient }) => {
    const useCronJobs = (options = {}) =>
        useQuery({
            queryKey: ['cronJobs'],
            queryFn: getCronJobs,
            refetchInterval: options.refetchInterval ?? 30 * 1000,
        });

    const useCronQueueStatus = (options = {}) =>
        useQuery({
            queryKey: ['cronQueueStatus'],
            queryFn: getCronQueueStatus,
            refetchInterval: options.refetchInterval ?? 30 * 1000,
        });

    const useCronQueueJobs = (options = {}) =>
        useQuery({
            queryKey: ['cronQueueJobs', options],
            queryFn: () => getCronQueueJobs(options),
            enabled: options.enabled ?? true,
            refetchInterval: options.refetchInterval ?? 30 * 1000,
        });

    const useRemoveCronQueueJob = () =>
        useMutation({
            mutationFn: removeCronQueueJob,
            onSettled: () => {
                queryClient.invalidateQueries({ queryKey: ['cronQueueJobs'] });
            },
        });

    const usePromoteCronQueueJob = () =>
        useMutation({
            mutationFn: promoteCronQueueJob,
            onSettled: () => {
                queryClient.invalidateQueries({ queryKey: ['cronQueueJobs'] });
            },
        });

    const useRunCronJob = () =>
        useMutation({
            mutationFn: runCronJob,
            onSettled: (_, __, jobName) => {
                queryClient.invalidateQueries({ queryKey: ['cronJobs'] });
                if (jobName) {
                    queryClient.invalidateQueries({ queryKey: ['cronJobHistory', jobName] });
                }
            },
        });

    const useAbortCronJob = () =>
        useMutation({
            mutationFn: abortCronJob,
            onSettled: (_, __, variables) => {
                queryClient.invalidateQueries({ queryKey: ['cronJobs'] });
                if (variables?.jobName) {
                    queryClient.invalidateQueries({ queryKey: ['cronJobHistory', variables.jobName] });
                }
            },
        });

    const useAbortAllCronJobs = () =>
        useMutation({
            mutationFn: abortAllCronJobs,
            onSettled: () => {
                queryClient.invalidateQueries({ queryKey: ['cronJobs'] });
                queryClient.invalidateQueries({ queryKey: ['cronJobHistory'] });
            },
        });

    const useToggleCronJob = () =>
        useMutation({
            mutationFn: toggleCronJob,
            onSettled: () => {
                queryClient.invalidateQueries({ queryKey: ['cronJobs'] });
            },
        });

    const useSetCronJobLane = () =>
        useMutation({
            mutationFn: setCronJobLane,
            onSettled: () => {
                queryClient.invalidateQueries({ queryKey: ['cronJobs'] });
            },
        });

    const useUpdateCronJobSchedule = () =>
        useMutation({
            mutationFn: updateCronJobSchedule,
            onSettled: () => {
                queryClient.invalidateQueries({ queryKey: ['cronJobs'] });
            },
        });

    const useUpdateCronJobTimeout = () =>
        useMutation({
            mutationFn: updateCronJobTimeout,
            onSettled: () => {
                queryClient.invalidateQueries({ queryKey: ['cronJobs'] });
            },
        });

    const useCronJobHistory = (jobName, options = {}) =>
        useQuery({
            queryKey: ['cronJobHistory', jobName, options],
            queryFn: () => getCronJobHistory(jobName, options),
            enabled: Boolean(jobName),
            refetchInterval: options.refetchInterval ?? 30000,
        });

    const useCronRunSteps = (jobName, runId, options = {}) =>
        useQuery({
            queryKey: ['cronRunSteps', jobName, runId, options],
            queryFn: () => getCronRunSteps(jobName, runId, options),
            enabled: Boolean(jobName && runId),
            refetchInterval: options.refetchInterval ?? 15000,
        });

    return {
        useCronJobs,
        useCronQueueStatus,
        useCronQueueJobs,
        useRemoveCronQueueJob,
        usePromoteCronQueueJob,
        useRunCronJob,
        useAbortCronJob,
        useAbortAllCronJobs,
        useToggleCronJob,
        useSetCronJobLane,
        useUpdateCronJobSchedule,
        useUpdateCronJobTimeout,
        useCronJobHistory,
        useCronRunSteps,
    };
};
